"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Analytics, AnalyticsEvent } from "@/lib/segment";
import { initDataDog } from "@/lib/datadog";
import { type SessionUserData, type SubsUserData } from "@/types/session";
import { ApiRoutes } from "@/services/constants";

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export function AnalyticsProvider({
  children,
}: Readonly<AnalyticsProviderProps>) {
  const pathname = usePathname();
  const [sessionData, setSessionData] = useState<SessionUserData | null>(null);

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        const sessionExistsResponse = await fetch(ApiRoutes.SESSION_EXISTS, {
          credentials: "include",
        });
        const { hasSession } = await sessionExistsResponse.json();

        if (!hasSession) {
          return;
        }

        const response = await fetch(ApiRoutes.SESSION, {
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok) {
          setSessionData(data);
        }
      } catch (error) {
        console.error(
          "Failed to fetch session data in analytics provider",
          error
        );
      }
    };

    fetchSessionData();
  }, []);

  useEffect(() => {
    initDataDog(sessionData ?? undefined);

    if (sessionData) {
      const subsUser = sessionData.userData as SubsUserData;

      Analytics.setGlobalContext({
        user_id: subsUser?.id,
        email: sessionData.email,
        user_type: sessionData.userType,
      });

      if (subsUser?.universalUserUuid) {
        Analytics.identify(subsUser.universalUserUuid);
      }
      Analytics.trackEvent(AnalyticsEvent.USER_LOGGED_IN, {
        email: sessionData.email,
        user_type: sessionData.userType,
      });
    }
  }, [sessionData]);

  useEffect(() => {
    const getPageName = (path: string) => {
      if (path === "/" || path === "" || path === "//") return "Home";
      
      let cleanPath = path.replace(/^\//, "");
      while (cleanPath.endsWith("/")) {
        cleanPath = cleanPath.slice(0, -1);
      }
      
      return cleanPath
        .replace(/_/g, " ")
        .replace(/-/g, " ")
        .replace(/\//g, " - ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase()) || "Home";
    };

    const pageName = getPageName(pathname);
    Analytics.pageViewed(pageName);
  }, [pathname]);

  return <>{children}</>;
}
